import { InfusedIngredientInput, PotencyResult } from '../types/recipe';

/**
 * Calculates the total cannabinoid milligrams for a single infused ingredient.
 *
 * Formula: materialWeight × (cannabinoidPercentage / 100) × 1000 × decarboxylationEfficiency × extractionEfficiency
 */
export function calculateIngredientMg(ingredient: InfusedIngredientInput): number {
  return (
    ingredient.materialWeight *
    (ingredient.cannabinoidPercentage / 100) *
    1000 *
    ingredient.decarboxylationEfficiency *
    ingredient.extractionEfficiency
  );
}

/**
 * Calculates total and per-serving potency for a list of infused ingredients.
 *
 * Returns totalMg (sum of all ingredient contributions), perServingMg (totalMg / servings),
 * and a byIngredient breakdown — all rounded to 2 decimal places.
 *
 * Returns {totalMg: 0, perServingMg: 0, byIngredient: []} when no infused ingredients are present.
 */
export function calculatePotency(
  infusedIngredients: InfusedIngredientInput[],
  servings: number
): PotencyResult {
  if (infusedIngredients.length === 0) {
    return { totalMg: 0, perServingMg: 0, byIngredient: [] };
  }

  const byIngredient = infusedIngredients.map((ingredient) => {
    const mg = calculateIngredientMg(ingredient);
    return {
      ingredientName: ingredient.name,
      cannabinoidType: ingredient.cannabinoidType,
      mg: Math.round(mg * 100) / 100,
    };
  });

  const totalMg = Math.round(
    byIngredient.reduce((sum, item) => sum + item.mg, 0) * 100
  ) / 100;

  const perServingMg = Math.round((totalMg / servings) * 100) / 100;

  return { totalMg, perServingMg, byIngredient };
}
